import React, { useReducer, useContext } from "react";
import reducer from "./reducer";
import axios from 'axios';
import {
    DISPLAY_ALERT,
    CLEAR_ALERT,
    LOGIN_USER_BEGIN,
    LOGIN_USER_ERROR,
    LOGIN_USER_SUCCESS,
    TOGGLE_SIDEBAR,
    LOGOUT_USER,
    UPDATE_USER_BEGIN,
    UPDATE_USER_ERROR,
    UPDATE_USER_SUCCESS,
    HANDLE_CHANGE,
    CLEAR_VALUES,
    CREATE_INFORMATION_ITEM_BEGIN,
    CREATE_INFORMATION_ITEM_ERROR,
    CREATE_INFORMATION_ITEM_SUCCESS,
    GET_INFORMATION_ITEMS_BEGIN,
    GET_INFORMATION_ITEMS_SUCCESS,
    SET_EDIT_INFORMATION_ITEM,
    DELETE_INFORMATION_ITEM_BEGIN,
    EDIT_INFORMATION_ITEM_BEGIN,
    EDIT_INFORMATION_ITEM_SUCCESS,
    EDIT_INFORMATION_ITEM_ERROR,
    CLEAR_FILTERS,
    CHANGE_PAGE,
    CHANGE_MONTH_TO_DISPLAY,
    CHANGE_ROW,
    CHANGE_SHOW_LOGOUT,
} from "./actions";

const token = localStorage.getItem('token')
const user = localStorage.getItem('user')

const initialState = {
    isLoading: false,
    showAlert: false,
    alertText: '',
    alertType: '',
    user: user ? JSON.parse(user) : null,
    token: token,
    showSidebar: false,
    isEditing: false,
    editInformationItemId: '',
    year: new Date().getFullYear(),
    // attention js uses January as 0 to December as 11
    // backend is selfimplemented and uses the german way (jan as 1 to dec as 12)
    month: new Date().getMonth() + 1,
    electricity: '',
    commentElectricity: '',
    photovoltaic: '',
    commentPhotovoltaic: '',
    water: '',
    commentWater: '',
    heating: '',
    commentHeating: '',
    informationItems: [],
    totalInformationItems: 0,
    yearToDisplay: new Date().getFullYear(),
    monthToDisplay: new Date().getMonth() + 1,
    rowToDisplay: 0,
    search: '',
    sort: 'DESC',
    sortOptions: ['DESC', 'ASC'],
    showLogout: false,
}

const AppContext = React.createContext()

const AppProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer,
        initialState);

    // axios
    const authFetch = axios.create({
        baseURL: '/dashboard-backend/api/v1/',
        // baseURL: '/api/v1/',
    })

    // request
    authFetch.interceptors.request.use((config) => {
        config.headers.common['Authorization'] = `Bearer ${state.token}`
        return config
    }, (error) => {
        return Promise.reject(error)
    })

    // response
    authFetch.interceptors.response.use((response) => {
        return response
    }, (error) => {
        console.log(error.response);
        if (error.response.status === 401) {
            logoutUser()
        }
        return Promise.reject(error)
    })

    const displayAlert = (customAlertText) => {
        dispatch({ type: DISPLAY_ALERT, text: customAlertText })
        clearAlert()
    }

    const clearAlert = () => {
        setTimeout(() => {
            dispatch({ type: CLEAR_ALERT })
        }, 4000)
    }

    const addUserToLocalStorage = ({ user, token }) => {
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', token);
    }

    const removeUserFromLocalStorage = () => {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
    }

    const loginUser = async (currentUser) => {
        dispatch({ type: LOGIN_USER_BEGIN })
        try {
            const { data } = await axios.post('/dashboard-backend/api/v1/auth/login', currentUser)
            // const { data } = await axios.post('/api/v1/auth/login', currentUser)
            const { user, token } = data
            dispatch({
                type: LOGIN_USER_SUCCESS,
                payload: {
                    user,
                    token
                }
            })
            addUserToLocalStorage({ user, token })
        } catch (error) {
            dispatch({
                type: LOGIN_USER_ERROR,
                payload: {
                    msg: error.response.data.msg
                }
            })
        }
        clearAlert()
    }

    const toggleSidebar = () => {
        dispatch({ type: TOGGLE_SIDEBAR })
    }

    const logoutUser = () => {
        dispatch({ type: LOGOUT_USER })
        removeUserFromLocalStorage();
    }

    const updateUser = async (currentUser) => {
        dispatch({ type: UPDATE_USER_BEGIN })
        try {
            const { data } = await authFetch.patch('auth/updateUser', currentUser)

            const { user, token } = data

            dispatch({ type: UPDATE_USER_SUCCESS, payload: { user, token } })
            addUserToLocalStorage({ user, token })
        } catch (error) {
            if (error.response.status !== 401) {
                dispatch({ type: UPDATE_USER_ERROR, payload: { msg: error.response.data.msg } })
            }
        }
        clearAlert()
    }

    const handleChange = ({ name, value }) => {
        dispatch({ type: HANDLE_CHANGE, payload: { name, value } })
    }

    const clearValues = () => {
        dispatch({ type: CLEAR_VALUES })
    }

    const createInformationItem = async () => {
        dispatch({ type: CREATE_INFORMATION_ITEM_BEGIN })

        try {
            const {
                month,
                year,
                electricity,
                commentElectricity,
                photovoltaic,
                commentPhotovoltaic,
                water,
                commentWater,
                heating,
                commentHeating,
            } = state
            await authFetch.post('/informationItem', {
                year: year,
                month: month,
                electricity: parseFloat(electricity.replace(',','.').replace(' ','')),
                commentElectricity,
                photovoltaic: parseFloat(photovoltaic.replace(',','.').replace(' ','')),
                commentPhotovoltaic,
                water: parseFloat(water.replace(',','.').replace(' ','')),
                commentWater,
                heating: parseFloat(heating.replace(',','.').replace(' ','')),
                commentHeating,
            })
            dispatch({ type: CREATE_INFORMATION_ITEM_SUCCESS })
            dispatch({ type: CLEAR_VALUES })
        } catch (error) {
            if (error.response.status === 401) {
                return
            }
            dispatch({
                type: CREATE_INFORMATION_ITEM_ERROR,
                payload: { msg: error.response.data.msg }
            })
        }
        clearAlert()
    }

    const getInformationItems = async () => {
        const { search, sort } = state

        let url = `/informationItem?sort=${sort}`

        if (search) {
            url = url + `&search=${search}`
        }

        dispatch({ type: GET_INFORMATION_ITEMS_BEGIN })
        try {
            const { data } = await authFetch.get(url)
            var { informationItems, totalInformationItems } = data
            dispatch({
                type: GET_INFORMATION_ITEMS_SUCCESS,
                payload: {
                    informationItems,
                    totalInformationItems
                }
            })
        } catch (error) {
            logoutUser()
            console.log(error)
        }

        clearAlert()
    }

    const setEditInformationItem = (id) => {
        dispatch({ type: SET_EDIT_INFORMATION_ITEM, payload: { id } })
    }

    const editInformationItem = async (
        id,
        electricity,
        commentElectricity,
        photovoltaic,
        commentPhotovoltaic,
        water,
        commentWater,
        heating,
        commentHeating) => {
        dispatch({ type: EDIT_INFORMATION_ITEM_BEGIN });

        try {
            await authFetch.patch(`/informationItem/${id}`,
                {
                    electricity: parseFloat(electricity.replace(',','.').replace(' ','')),
                    commentElectricity,
                    photovoltaic: parseFloat(photovoltaic.replace(',','.').replace(' ','')),
                    commentPhotovoltaic,
                    water: parseFloat(water.replace(',','.').replace(' ','')),
                    commentWater,
                    heating: parseFloat(heating.replace(',','.').replace(' ','')),
                    commentHeating
                });
            dispatch({ type: EDIT_INFORMATION_ITEM_SUCCESS });
            getInformationItems();
            dispatch({ type: CLEAR_VALUES });
        } catch (error) {
            if (error.response.status === 401) {
                return
            }
            dispatch({
                type: EDIT_INFORMATION_ITEM_ERROR,
                payload: { msg: error.response.data.msg }
            })
            clearAlert()
        }
    }

    const deleteInformationItem = async (informationItemId) => {
        dispatch({ type: DELETE_INFORMATION_ITEM_BEGIN })
        try {
            await authFetch.delete(`/informationItem/${informationItemId}`)
            getInformationItems()
        } catch (error) {
            logoutUser()
        }
    }

    const clearFilters = () => {
        dispatch({ type: CLEAR_FILTERS })
    }

    const changeYearToDisplay = (yearToDisplay) => {
        dispatch({ type: CHANGE_PAGE, payload: { yearToDisplay } })
    }

    const changeMonthToDisplay = (monthToDisplay) => {
        dispatch({ type: CHANGE_MONTH_TO_DISPLAY, payload: { monthToDisplay } })
    }

    const changeRowToDisplay = (rowToDisplay) => {
        dispatch({ type: CHANGE_ROW, payload: { rowToDisplay } })
    }

    const changeShowLogout = (showLogout) => {
        dispatch({ type: CHANGE_SHOW_LOGOUT, payload: { showLogout } })
    }

    return <AppContext.Provider value={
        {
            ...state,
            displayAlert,
            loginUser,
            toggleSidebar,
            logoutUser,
            updateUser,
            handleChange,
            clearValues,
            createInformationItem,
            getInformationItems,
            setEditInformationItem,
            deleteInformationItem,
            editInformationItem,
            clearFilters,
            changeYearToDisplay,
            changeMonthToDisplay,
            changeRowToDisplay,
            changeShowLogout,
        }}>
        {children}
    </AppContext.Provider>
}

export const useAppContext = () => {
    return useContext(AppContext);
}

export { AppProvider, initialState }